import React, { useState, useCallback } from 'react';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import ModulesPage from './pages/ModulesPage';
import TheResetPage from './pages/TheResetPage';
import AudioLibraryPage from './pages/AudioLibraryPage';
import BooksPage from './pages/BooksPage';
import NotesPage from './pages/NotesPage';
import ProfilePage from './pages/ProfilePage';
import InstallPrompt from './components/InstallPrompt';
import { User, Membership } from './types';
import { lessons } from './data/mockData';
import LessonDetailPage from './pages/LessonDetailPage';
import ProtocolsPage from './pages/ProtocolsPage';
import { LanguageProvider } from './i18n/LanguageContext';
import { InstallProvider } from './components/InstallProvider';


const App: React.FC = () => {
  const [currentView, setCurrentView] = useState('home');
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const login = (membership: Membership) => {
    setUser({
      id: 'uid123',
      name: membership === Membership.Premium ? 'Breathe+ User' : 'Free User',
      membership: membership,
    });
    setCurrentView('home');
  };

  const logout = () => {
    setUser(null);
    setCurrentView('home');
  };

  const navigate = useCallback((view: string, slug: string | null = null) => {
    setCurrentView(view);
    setActiveSlug(slug);
    window.scrollTo(0, 0);
  }, []);

  const renderContent = () => {
    if (currentView.startsWith('lesson-')) {
        const lesson = lessons.find(l => l.slug === activeSlug);
        if (lesson) {
            return <LessonDetailPage lesson={lesson} user={user} navigate={navigate} />;
        }
    }
    
    switch (currentView) {
      case 'home':
        return <HomePage navigate={navigate} user={user} />;
      case 'modules':
        return <ModulesPage navigate={navigate} user={user} />;
      case 'the-reset':
        return <TheResetPage navigate={navigate} />;
      case 'protocols':
        return <ProtocolsPage />;
      case 'audio':
        return <AudioLibraryPage user={user} navigate={navigate} />;
      case 'books':
        return <BooksPage user={user} />;
      case 'notes':
        return <NotesPage user={user} />;
      case 'profile':
        return <ProfilePage user={user} logout={logout} login={login} />;
      default:
        return <HomePage navigate={navigate} user={user} />;
    }
  };

  return (
    <LanguageProvider>
      <InstallProvider>
        <div className="min-h-screen bg-gradient-to-b from-primary to-secondary font-sans text-stone-800">
          <Header user={user} navigate={navigate} login={login} />
          <main className="px-4 py-8 sm:px-6 lg:px-8">
            {renderContent()}
          </main>
          <footer className="text-center py-8 text-stone-500">
            <p>&copy; 2025 Breathe to Become. All rights reserved.</p>
          </footer>
          <InstallPrompt />
        </div>
      </InstallProvider>
    </LanguageProvider>
  );
};

export default App;